import type { LucideIcon } from "lucide-react";
import {
  Megaphone,
  ShieldCheck,
  Phone,
  Workflow,
  FileText,
  ClipboardList,
  MessageSquareText,
  Tags,
  RefreshCw,
} from "lucide-react";
import { Link } from "react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

// Settings hub (docs/15 §4) — the configuration home. Tiles the 8 sub-sections.
// Slice 0 is the landing shell: sub-sections whose engine + UI already exist link
// to their screen; the rest are labelled "coming soon" (no content built here).

type Status = "Built" | "Partial" | "Coming soon";
type Section = {
  title: string;
  icon: LucideIcon;
  desc: string;
  status: Status;
  to?: string; // present once a screen exists to open
};

// Status tracks docs/15 §4 (engine built vs. admin UI present). "to" is set only
// where a real screen exists today (the Cadence Config page covers cadence +
// shows the automation rules).
const SECTIONS: Section[] = [
  {
    title: "Campaign settings",
    icon: Megaphone,
    desc: "Drip campaigns — ordered multi-step SMS / email / task / mail sequences.",
    status: "Built",
    to: "/settings/campaigns",
  },
  {
    title: "Automations",
    icon: Workflow,
    desc: "Tasks and drips fired per lead status / deal stage — the automation-rules engine.",
    status: "Built",
    to: "/settings/automations",
  },
  {
    title: "User management & permissions",
    icon: ShieldCheck,
    desc: "Users, roles, and the editable permission matrix (RBAC).",
    status: "Built",
    to: "/settings/users",
  },
  {
    title: "Phone settings",
    icon: Phone,
    desc: "Numbers, incoming-call flow, and dialer configuration.",
    status: "Built",
    to: "/settings/phone",
  },
  {
    title: "Templates",
    icon: FileText,
    desc: "SMS, email, and task templates with merge fields and preview.",
    status: "Built",
    to: "/settings/templates",
  },
  {
    title: "Forms",
    icon: ClipboardList,
    desc: "Configurable intake forms for leads, buyers, agents, and dispo.",
    status: "Coming soon",
  },
  {
    title: "Scripts",
    icon: MessageSquareText,
    desc: "Configurable caller scripts with merge fields — picked on the lead page mid-call.",
    status: "Built",
    to: "/settings/scripts",
  },
  {
    title: "Tags management",
    icon: Tags,
    desc: "Create and organize tags — name, category, color, scope.",
    status: "Built",
    to: "/settings/tags",
  },
  {
    title: "DMStack sync",
    icon: RefreshCw,
    desc: "Which DMStack cases feed the CRM + how their fields map (WS-7 connector).",
    status: "Built",
    to: "/settings/sync",
  },
];

const statusClass: Record<Status, string> = {
  Built: "bg-green-100 text-green-800 dark:bg-green-950/60 dark:text-green-300",
  Partial: "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300",
  "Coming soon": "bg-muted text-muted-foreground",
};

const SectionTile = ({ s }: { s: Section }) => {
  const body = (
    <Card
      className={`h-full transition-colors ${
        s.to ? "hover:border-primary/50 cursor-pointer" : "opacity-70"
      }`}
    >
      <CardContent className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="rounded-md bg-muted p-2">
            <s.icon className="h-5 w-5 text-foreground/80" />
          </div>
          <Badge className={statusClass[s.status]}>{s.status}</Badge>
        </div>
        <div className="font-medium">{s.title}</div>
        <p className="text-sm text-muted-foreground">{s.desc}</p>
      </CardContent>
    </Card>
  );
  return s.to ? (
    <Link to={s.to} className="no-underline">
      {body}
    </Link>
  ) : (
    body
  );
};

const SettingsHubPage = () => (
  <div className="p-2 max-w-5xl">
    <h1 className="text-xl font-semibold">Settings</h1>
    <div className="text-sm text-muted-foreground mb-4">
      The configuration hub — business logic as editable data. Open a section to manage it.
    </div>
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {SECTIONS.map((s) => (
        <SectionTile key={s.title} s={s} />
      ))}
    </div>
  </div>
);

SettingsHubPage.path = "/settings";
export default SettingsHubPage;
